/* Form-family park: the dropzone converts with the upload round (component-map.md). */
import { Flex, useColorModeValue, useFormControl, useFormControlContext } from '@chakra-ui/react';
import { ArrowUpOnSquareStackIcon } from '@heroicons/react/24/outline';
import { Text } from '@main/complykit/components/text';
import { forwardRef, InputHTMLAttributes } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { useOptional } from '../hooks';
import { EmptyPlaceholder } from '../placeholder';
import { errorToast } from '../toast';
import { filterFilesByAccept } from './file-accept';

export type DropzoneProps = {
  onChange?: (files: File[]) => void;
  onDragLeave?: () => void;
  isMulti?: boolean;
  topLabelText?: string;
  bottomLabelText?: string;
  constraintText?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  /** Shows the files without taking new ones; a ComplyKit Field has no form context to say so. */
  isReadOnly?: boolean;
};

export const Dropzone: React.FC<DropzoneProps> = ({
  onChange,
  onDragLeave,
  topLabelText,
  bottomLabelText,
  constraintText,
  isMulti,
  inputProps,
  isReadOnly = false,
}) => {
  const { t } = useTranslation('ui');
  const { readOnly, id } = useFormControl({});
  const formControl = useOptional(useFormControlContext);
  const isInvalid = formControl?.isInvalid || inputProps?.['aria-invalid'] === true;

  const handleFiles = (files: File[]) => {
    const { acceptedFiles, rejectedFiles, acceptedTypes } = filterFilesByAccept(
      files,
      inputProps?.accept,
    );

    if (rejectedFiles.length) {
      errorToast(
        t('upload.invalidFileType', {
          types: acceptedTypes.join(', '),
          interpolation: { escapeValue: false },
        }),
      );
    }

    if (acceptedFiles.length || !rejectedFiles.length) {
      onChange?.(acceptedFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    multiple: isMulti,
    disabled: !!readOnly || isReadOnly,
  });
  const dropzoneInputProps = getInputProps();

  const borderColor = useColorModeValue('#EBEBEB', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'gray.200');

  return (
    <Flex width="full" direction="column" height="full" minH="185px" gap={2}>
      <Flex
        direction="column"
        width="full"
        height="full"
        padding={8}
        border="1px"
        borderColor={isDragActive ? 'blue.500' : isInvalid ? 'red.500' : borderColor}
        borderStyle="dashed"
        borderRadius="8px"
        textColor={textColor}
        justifyContent="center"
        alignItems="center"
        cursor="pointer"
        {...getRootProps()}
        position="relative"
      >
        <DropzoneInput
          {...dropzoneInputProps}
          id={id}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: 0,
            top: 0,
            opacity: '0',
          }}
          title=""
          onDragLeave={() => onDragLeave && onDragLeave()}
          {...inputProps}
          onChange={(event) => {
            const { rejectedFiles } = filterFilesByAccept(
              Array.from(event.target.files ?? []),
              inputProps?.accept,
            );

            dropzoneInputProps.onChange?.(event);

            if (rejectedFiles.length) {
              event.currentTarget.value = '';
            }
          }}
        />
        <EmptyPlaceholder py={0}>
          <EmptyPlaceholder.Icon as={ArrowUpOnSquareStackIcon} />
          <EmptyPlaceholder.Content px="0">
            <EmptyPlaceholder.Heading>
              {topLabelText || t('upload.dropzone.topLabel')}
            </EmptyPlaceholder.Heading>
            <EmptyPlaceholder.Subheading>
              {bottomLabelText || t('upload.dropzone.bottomLabel')}
            </EmptyPlaceholder.Subheading>
          </EmptyPlaceholder.Content>
        </EmptyPlaceholder>
      </Flex>

      <Text size="xs" color="muted">
        {constraintText || t('upload.dropzone.constraintLabel')}
      </Text>
    </Flex>
  );
};

export const DropzoneInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => {
    return <input type="file" ref={ref} {...props} />;
  },
);
